import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Check, ArrowRight, CreditCard, Shield, Zap, Building, Banknote, Lock } from 'lucide-react';

const PricingPage = () => {
  const plans = [
    {
      name: "Free",
      subtitle: "with Payment Processing",
      price: "$0",
      period: "/month",
      description: "Full platform access when you use our integrated payment processing.",
      features: [
        "Unlimited customers & properties",
        "Scheduling & route optimization",
        "Crew management & mobile app",
        "Estimates & invoicing",
        "Reporting & analytics",
        "Unlimited users",
        "Email support"
      ],
      cta: "Get Started Free",
      popular: true,
      highlight: "Most Popular"
    },
    {
      name: "Professional",
      subtitle: "Bring Your Own Processor",
      price: "$129",
      period: "/month",
      description: "Use your existing payment processor with full platform access.",
      features: [
        "Everything in Free tier",
        "Use any payment processor",
        "Priority email support",
        "Phone support",
        "Custom integrations",
        "Advanced reporting"
      ],
      cta: "Start Free Trial",
      popular: false,
      highlight: null
    },
    {
      name: "Enterprise",
      subtitle: "Custom Solutions",
      price: "Custom",
      period: "",
      description: "Tailored solutions for large landscaping operations.",
      features: [
        "Everything in Professional",
        "Dedicated account manager",
        "Custom development",
        "SLA guarantees",
        "On-premise deployment option",
        "White-label options",
        "API access"
      ],
      cta: "Contact Sales",
      popular: false,
      highlight: null
    }
  ];

  const paymentFeatures = [
    {
      icon: CreditCard,
      title: "Credit & Debit Cards",
      description: "Accept Visa, Mastercard, American Express, and Discover"
    },
    {
      icon: Banknote,
      title: "ACH Bank Transfers",
      description: "Lower fees for recurring payments and larger invoices"
    },
    {
      icon: Lock,
      title: "Tokenization & Security",
      description: "PCI-compliant card storage for repeat billing"
    },
    {
      icon: Shield,
      title: "Fraud Protection",
      description: "Built-in fraud detection and chargeback management"
    },
    {
      icon: Zap,
      title: "Fast Deposits",
      description: "Funds deposited within 1-2 business days"
    },
    {
      icon: Building,
      title: "Business Dashboard",
      description: "Track all transactions, refunds, and deposits in one place"
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get the full Orchard LMS platform free with our integrated payment processing, 
            or choose a flat monthly fee to use your own processor.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.popular ? 'border-2 border-primary shadow-xl scale-105' : 'border-gray-200'}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                      {plan.highlight}
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pt-8 pb-4">
                  <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
                  <p className="text-sm text-gray-500">{plan.subtitle}</p>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-xl text-gray-500">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
                </CardHeader>
                <CardContent className="pt-4 pb-8">
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={plan.name === "Enterprise" ? "/contact" : "/signup"}>
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                      size="lg"
                    >
                      {plan.cta} {plan.name !== "Enterprise" && <ArrowRight className="ml-2 h-4 w-4" />}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Processing Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Integrated Payment Processing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Accept payments directly through invoices with our built-in payment processing. 
              More than just credit cards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paymentFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Is the Free plan really free?</h3>
              <p className="text-gray-600">Yes! You get full access to all platform features at no monthly cost. We earn a small percentage on payment processing when your customers pay invoices through our system.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">What payment methods can I accept?</h3>
              <p className="text-gray-600">Our integrated processing supports all major credit and debit cards, ACH bank transfers, and secure tokenized payments for recurring billing. This gives your customers flexible payment options while reducing your costs on larger transactions.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">What if I want to use my own payment processor?</h3>
              <p className="text-gray-600">No problem! The Professional plan at $129/month gives you full platform access while letting you use any payment processor you prefer. You can still send invoices through Orchard LMS and manually record payments.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Is there a limit to how many users I can add?</h3>
              <p className="text-gray-600">No limits on any plan! Add as many team members as you need—office staff, managers, and crew members—all included in your subscription.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Can I switch plans later?</h3>
              <p className="text-gray-600">Absolutely. You can upgrade, downgrade, or switch between the Free and Professional plans at any time. Your data stays intact regardless of which plan you're on.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start using Orchard LMS today. No credit card required for the free plan.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default PricingPage;
